import React, { ReactNode, useState } from "react";
import Header from "@cloudscape-design/components/header";
import { VehicleMasterTable } from "../../w_vehicles/data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  CollectionPreferences,
  CollectionPreferencesProps,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import { Link } from "react-router-dom";
import Icon from "@cloudscape-design/components/icon";
import StatusIndicator from "@cloudscape-design/components/status-indicator";

interface CellProps {
  Status: Boolean;
}

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  VehicleMaster: VehicleMasterTable[];
}

const DeviceStatus: React.FC<CellProps> = ({ Status }) => {
  let statusType: "success" | "error" = "error"; // status types (status indicator cloudscape)

  // Conditional rendering logic
  if (Status === true) {
    statusType = "success";
  } else if (Status === false) {
    statusType = "error";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success"
        ? "Active"
        : statusType === "error"
        ? "Deactivated"
        : "Unknown Status"}
    </StatusIndicator>
  );
};
// const DeviceStatus: React.FC<CellProps> = ({ status }) => {
//   let statusType: "success" | "error" = "error"; // status types (status indicator cloudscape)

//   // Conditional rendering logic
//   if (Status === "Active") {
//     statusType = "success";
//   } else if (Status === "Deactivated") {
//     statusType = "error";
//   }

//   return (
//     <StatusIndicator type={statusType}>
//       {statusType === "success"
//         ? "Active"
//         : statusType === "error"
//         ? "Deactivated"
//         : "Unknown Status"}
//     </StatusIndicator>
//   );
// };

const columnDefinitions: TableProps<VehicleMasterTable>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "ID",
    width: "10%",
  },

  {
    header: "Vehicle Code",
    cell: ({ VehicleCode }) => (
      <Link to={`/vehicles/${VehicleCode}`}>
        {/* <Link to={`/vehicles/alarms/${VehicleCode}`}> */}
        {VehicleCode}
      </Link>
    ),
    sortingField: "VehicleCode",
    width: 100,
  },
  {
    header: "Type ",
    cell: ({ TypeVehicle }) => TypeVehicle,
    sortingField: "TypeVehicle",
    width: 100,
  },
  {
    header: "Category",
    cell: ({ Category }) => Category,
    sortingField: "TypeVehicle",
    width: 100,
  },
  {
    header: "Location ",
    cell: ({ Location }) => Location,
    sortingField: "Location",
    width: 100,
  },
  {
    header: "Status ",
    cell: DeviceStatus,
    sortingField: "Status",
    width: 120,
  },
  {
    header: "Last Update",
    cell: ({ Timestamps }) => new Date(Timestamps).toLocaleString(), // Display timestamp in a readable format
    sortingField: "Timestamps",
    sortingComparator: (a, b) =>
      new Date(a.Timestamps).getTime() - new Date(b.Timestamps).getTime(), // Compare timestamps as numbers
    minWidth: 150,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly VehicleMasterTable[] = [],
  selectedItems: readonly VehicleMasterTable[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VehicleList({ VehicleMaster }: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 10 });
  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<VehicleMasterTable>(VehicleMaster, {
    filtering: {
      filteringFunction: (item, filteringText) => {
        const filterTextLower = filteringText.toLowerCase();
        const statusText = item.Status ? "active" : "deactivated";
        if (statusText.includes(filterTextLower)) {
          return true;
        }

        return (
          item.VehicleCode.toLowerCase().includes(filterTextLower) ||
          item.TypeVehicle.toLowerCase().includes(filterTextLower) ||
          item.Location.toLowerCase().includes(filterTextLower) ||
          item.Category.toLowerCase().includes(filterTextLower) ||
          item.id.toLowerCase().includes(filterTextLower) ||
          item.Timestamps.toLowerCase().includes(filterTextLower)
        );
      },
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });

  return (
    <>
      <Table<VehicleMasterTable>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Vehicle table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              VehicleMaster,
              collectionProps.selectedItems,
              items.length
            )}
          >
            Vehicles
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Vehicle'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        // selectionType="multi"
      />
    </>
  );
}
